import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, Eye, EyeOff, Copy, RefreshCw, KeyRound, QrCode, Plus, Trash2,
  GripVertical, Mic, Upload, FolderOpen, User as UserIcon, Lock, Phone, Search, Check, Loader2,
} from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import { ProvisionProgressModal } from '../components/ProvisionProgressModal';
import { ForwardingPanel } from './ForwardingProfiles';
import AudioPicker from '../components/AudioPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

type Tab = 'general' | 'talk' | 'forwarding' | 'schedule' | 'phone' | 'blf' | 'voicemail' | 'view' | 'options';
const TABS: [Tab, string][] = [
  ['general',    'General'],
  ['talk',       'Stratus Talk'],
  ['forwarding', 'Call Forwarding'],
  ['schedule',   'Schedule'],
  ['phone',      'IP Phone'],
  ['blf',        'BLF'],
  ['voicemail',  'Voicemail'],
  ['view',       'View'],
  ['options',    'Options'],
];

export default function UserEditor() {
  const { extension_uuid: routeId } = useParams<{ extension_uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('general');

  // /users/new doesn't supply a :extension_uuid param, so detect by path too.
  const isNew = !routeId && /\/users\/new\/?$/.test(location.pathname);
  const extension_uuid = routeId || (isNew ? 'new' : '');

  if (!extension_uuid) return <Navigate to="/users" />;

  const user = useQuery<any>({
    queryKey: ['user-summary', extension_uuid],
    queryFn: async () => {
      const list = await fJson('/api/users');
      return Array.isArray(list) ? list.find((u: any) => u.extension_uuid === extension_uuid) : null;
    },
    enabled: !isNew,
  });

  // In create-mode the non-General tabs need a saved user before they can load.
  const lockedTab = (t: Tab) => isNew && t !== 'general';
  function pickTab(t: Tab) {
    if (lockedTab(t)) {
      toast('error', 'Save the user first — the other tabs need an extension to attach to.');
      return;
    }
    setTab(t);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/users')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Users</button>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          {isNew ? <><UserIcon size={18}/> Add User</> : <>Edit User {user.data?.extension ? `${user.data.extension}` : ''}</>}
        </h1>
        <SaveBadge tab={tab} extension_uuid={extension_uuid} qc={qc} toast={toast} />
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(([v, l]) => {
          const locked = lockedTab(v);
          return (
            <button
              key={v}
              onClick={() => pickTab(v)}
              title={locked ? 'Save the user first to unlock this tab' : undefined}
              className={`px-4 py-2 text-sm whitespace-nowrap inline-flex items-center gap-1 ${
                tab === v ? 'border-b-2 border-primary text-primary'
                : locked ? 'text-textSecondary/40 cursor-not-allowed'
                : 'text-textSecondary hover:text-textPrimary'
              }`}
            >
              {l}
              {locked && <Lock size={10} />}
            </button>
          );
        })}
      </div>

      {tab === 'general' && (isNew
        ? <NewUserGeneralPanel toast={toast} navigate={navigate} qc={qc} />
        : <GeneralTab extension_uuid={extension_uuid} qc={qc} toast={toast} />)}
      {!isNew && tab === 'talk'       && <TalkTab       extension_uuid={extension_uuid} qc={qc} toast={toast} />}
      {!isNew && tab === 'forwarding' && <ForwardingPanel extension_uuid={extension_uuid} />}
      {!isNew && tab === 'schedule'   && <ScheduleTab   extension_uuid={extension_uuid} qc={qc} toast={toast} />}
      {!isNew && tab === 'phone'      && <IPPhoneTab    extension_uuid={extension_uuid} qc={qc} toast={toast} navigate={navigate} />}
      {!isNew && tab === 'blf'        && <BLFTab        extension_uuid={extension_uuid} qc={qc} toast={toast} />}
      {!isNew && tab === 'voicemail'  && <VoicemailTab  extension_uuid={extension_uuid} qc={qc} toast={toast} />}
      {!isNew && tab === 'view'       && <ViewTab       extension_uuid={extension_uuid} qc={qc} toast={toast} />}
      {!isNew && tab === 'options'    && <OptionsTab    extension_uuid={extension_uuid} qc={qc} toast={toast} />}
    </div>
  );
}

// Add User — General tab in create mode. Same visual layout as the edit-mode
// General tab but POSTs to /api/users on save and redirects to the editor.
function NewUserGeneralPanel({ toast, navigate, qc }: any) {
  const me = api.me();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [form, setForm] = useState<any>({
    extension: '', email: '', first_name: '', last_name: '', mobile: '',
    outbound_caller_id: '', role: 'User',
    domain_uuid: me?.domain_uuid || '',
    assigned_dids: [],
    enable_2fa: false,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  // Suggest the next free extension once the domain is known.
  useQuery({
    queryKey: ['next-ext-new', form.domain_uuid],
    enabled: !!form.domain_uuid && !form.extension,
    queryFn: async () => {
      const r = await api.users.nextExtension(form.domain_uuid);
      if (r?.next && !form.extension) setForm((f: any) => ({ ...f, extension: r.next }));
      return r;
    },
  });

  const create = useMutation({
    mutationFn: () => api.users.create({
      domain_uuid: form.domain_uuid,
      extension: form.extension,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      mobile: form.mobile,
      role: form.role,
      enabled: 'true',
    }),
    onSuccess: async (r: any) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast('success', `User ${form.extension} created`);
      // 2FA default flag — store via bulk-2fa with a single id
      if (form.enable_2fa) {
        await api.users.bulk2fa([r.extension_uuid], true).catch(() => {});
      }
      navigate(`/users/${r.extension_uuid || r.id}/edit`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-6">
      <div className="grid grid-cols-[200px_1fr] gap-6">
        {/* Left col: avatar + 2FA toggle */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-32 h-32 rounded-full bg-elevated flex items-center justify-center text-textSecondary">
            <UserIcon size={56} />
          </div>
          <label className="text-sm flex items-center gap-2 self-start">
            <input type="checkbox" checked={!!form.enable_2fa} onChange={e => set('enable_2fa', e.target.checked)} />
            Enable 2FA
          </label>
          <p className="text-xs text-textSecondary">A SIP password is auto-generated on save. Reset later from the user editor.</p>
        </div>
        {/* Right col: form */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Extension *">
            <input className="input" value={form.extension} onChange={e => set('extension', e.target.value)} placeholder="1001" autoFocus />
          </Field>
          <Field label="Email">
            <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="user@example.com" />
          </Field>
          <Field label="First Name">
            <input className="input" value={form.first_name} onChange={e => set('first_name', e.target.value)} />
          </Field>
          <Field label="Last Name">
            <input className="input" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
          </Field>
          <Field label="Mobile">
            <input className="input" value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="+1..." />
          </Field>
          <Field label="Outbound Caller ID">
            <select className="input" value={form.outbound_caller_id} onChange={e => set('outbound_caller_id', e.target.value)}>
              <option value="">— Use department default —</option>
            </select>
          </Field>
          <Field label="Role">
            <select className="input" value={form.role} onChange={e => set('role', e.target.value)}>
              {['User', 'Receptionist', 'Manager', 'System Admin', 'System Owner'].map(r => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Main Department Membership ⓘ">
            <select className="input" value={form.domain_uuid} onChange={e => set('domain_uuid', e.target.value)}>
              <option value="">— Choose —</option>
              {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Assigned DID number(s)">
              <input className="input" value={(form.assigned_dids || []).join(', ')}
                     onChange={e => set('assigned_dids', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
                     placeholder="+19895551234, +19895555678" />
            </Field>
          </div>
          <div className="col-span-2 flex justify-end pt-2">
            <button
              onClick={() => create.mutate()}
              disabled={!form.extension || !form.domain_uuid || create.isPending}
              className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Save size={14} /> {create.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveBadge({ tab, extension_uuid, qc, toast }: any) {
  // The save action lives within each tab; this is just a visual cue + scroll-to-top reminder.
  return (
    <div className="ml-auto text-xs text-textSecondary">
      Tab: <span className="font-medium text-textPrimary">{TABS.find(t => t[0] === tab)?.[1]}</span>
    </div>
  );
}

// ── 1. General ──────────────────────────────────────────────────────────────
function GeneralTab({ extension_uuid, qc, toast }: any) {
  const user = useQuery<any>({
    queryKey: ['user-detail-full', extension_uuid],
    queryFn: () => fJson(`/api/users/${extension_uuid}`),
  });
  const settings = useQuery<any>({
    queryKey: ['user-settings', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`),
  });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [form, setForm] = useState<any>({});
  useEffect(() => {
    if (user.data) setForm({
      extension: user.data.extension,
      email: user.data.email || '',
      first_name: user.data.directory_first_name || '',
      last_name: user.data.directory_last_name || '',
      mobile: user.data.mobile || '',
      outbound_caller_id: settings.data?.outbound_caller_id || '',
      role: user.data.role || 'User',
      domain_uuid: user.data.domain_uuid || '',
      assigned_dids: user.data.assigned_dids || [],
      enable_2fa: !!settings.data?.two_fa_enabled,
    });
  }, [user.data?.extension_uuid, settings.data?.extension_uuid]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/users/${extension_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-detail-full', extension_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const resetPassword = useMutation({
    mutationFn: () => fJson(`/api/users/${extension_uuid}/reset-password`, { method: 'POST' }),
    onSuccess: (r: any) => toast('success', `New password: ${r.password || 'sent by email'}`),
    onError: (e: any) => toast('error', e.message),
  });

  const [qrModal, setQrModal] = useState<{ url: string; qr_data_url: string | null } | null>(null);
  const welcomeEmail = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/welcome-email`, { method: 'POST' }),
    onSuccess: (r: any) => {
      if (r.url) {
        navigator.clipboard.writeText(r.url).catch(() => {});
        // Open the QR modal so the user can scan with a phone camera. Falls back
        // to text if QR generation failed.
        setQrModal({ url: r.url, qr_data_url: r.qr_data_url || null });
        toast('success', r.sent ? 'Welcome email sent + link copied' : 'Welcome link copied');
      } else toast('success', 'Welcome email sent');
    },
    onError: (e: any) => toast('error', e.message),
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <div className="card p-6">
      <div className="grid grid-cols-[200px_1fr] gap-6">
        {/* Left col: avatar + buttons */}
        <div className="flex flex-col items-center gap-3">
          <div className="w-32 h-32 rounded-full bg-elevated flex items-center justify-center text-textSecondary">
            <span className="text-5xl">👤</span>
          </div>
          <button className="btn-ghost w-full" disabled={resetPassword.isPending}
                  onClick={() => confirm('Reset password and email it to the user?') && resetPassword.mutate()}>
            {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
          </button>
          <button className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  disabled={welcomeEmail.isPending}
                  onClick={() => welcomeEmail.mutate()}>
            <QrCode size={14} /> {welcomeEmail.isPending ? 'Sending…' : 'Welcome / QR'}
          </button>
          <label className="text-sm flex items-center gap-2 self-start">
            <input type="checkbox" checked={!!form.enable_2fa} onChange={e => set('enable_2fa', e.target.checked)} disabled />
            Enable 2FA
          </label>
          <p className="text-xs text-textSecondary">2FA is self-only — admins can disable but not enable.</p>
        </div>
        {/* Right col: form */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Extension"><input className="input" value={form.extension || ''} disabled /></Field>
          <Field label="Email"><input className="input" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="First Name"><input className="input" value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} /></Field>
          <Field label="Last Name"><input className="input" value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} /></Field>
          <Field label="Mobile"><input className="input" value={form.mobile || ''} onChange={e => set('mobile', e.target.value)} /></Field>
          <Field label="Outbound Caller ID"><input className="input" value={form.outbound_caller_id || ''} onChange={e => set('outbound_caller_id', e.target.value)} /></Field>
          <Field label="Role">
            <select className="input" value={form.role || 'User'} onChange={e => set('role', e.target.value)}>
              {['User', 'Receptionist', 'Manager', 'System Admin', 'System Owner'].map(r => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Main Department Membership ⓘ">
            <select className="input" value={form.domain_uuid || ''} onChange={e => set('domain_uuid', e.target.value)}>
              <option value="">— Choose —</option>
              {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
            </select>
          </Field>
          <div className="col-span-2">
            <Field label="Assigned DID number(s)">
              <input className="input" value={(form.assigned_dids || []).join(', ')} onChange={e => set('assigned_dids', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} />
            </Field>
          </div>
          <div className="col-span-2 flex justify-end pt-2">
            <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
          </div>
        </div>
      </div>
      {qrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setQrModal(null)}>
          <div className="card max-w-sm w-full p-6 space-y-4 text-center" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold">Welcome QR</h2>
            <p className="text-sm text-textSecondary">Scan this with the user's phone camera to open the one-time provisioning link. Already copied to clipboard.</p>
            {qrModal.qr_data_url ? (
              <img src={qrModal.qr_data_url} alt="QR code" className="mx-auto bg-white p-2 rounded" style={{ width: 256, height: 256 }} />
            ) : (
              <div className="text-sm text-warning">QR generation unavailable on backend; URL only.</div>
            )}
            <div className="font-mono text-xs break-all bg-elevated p-2 rounded">{qrModal.url}</div>
            <button className="btn-primary w-full" onClick={() => setQrModal(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 2. Stratus Talk ─────────────────────────────────────────────────────────
function TalkTab({ extension_uuid, qc, toast }: any) {
  const cfg = useQuery<any>({
    queryKey: ['talk-config', extension_uuid],
    queryFn: () => fJson(`/api/talk/config/${extension_uuid}`).catch(() => ({})),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/talk/config/${extension_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['talk-config', extension_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const stratusHost = window.location.host;

  return (
    <div className="card p-6 space-y-4 max-w-2xl">
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
        Enable Stratus Talk and Meet to connect with a single click in your email signature and website
      </label>
      <Field label="Your Link Name:">
        <input className="input" value={f.link_name || ''} onChange={e => set('link_name', e.target.value)} placeholder="firstname-lastname" />
      </Field>
      <Field label="Talk URL">
        <a href={`https://${stratusHost}/${f.link_name || ''}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all">
          https://{stratusHost}/{f.link_name || '<slug>'} <Copy size={12} />
        </a>
      </Field>
      <Field label="Ask customer for">
        <select className="input" value={f.ask_for || 'name_email'} onChange={e => set('ask_for', e.target.value)}>
          <option value="name_email">Name & Email Address</option>
          <option value="name">Name only</option>
          <option value="nothing">Nothing</option>
        </select>
      </Field>
      <Field label="Meet URL">
        <a href={`https://${stratusHost}/meet/${f.link_name || ''}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all">
          https://{stratusHost}/meet/{f.link_name || '<slug>'} <Copy size={12} />
        </a>
      </Field>
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!f.approve_participants} onChange={e => set('approve_participants', e.target.checked)} />
        Approve participants before they join
      </label>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── 3. Forwarding ─ rendered inline via the imported ForwardingPanel above. ──

// ── 4. Schedule ─────────────────────────────────────────────────────────────
function ScheduleTab({ extension_uuid, qc, toast }: any) {
  const settings = useQuery<any>({
    queryKey: ['user-settings', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(settings.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings', extension_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-6 space-y-4 max-w-2xl">
      <div className="font-medium">Working hours</div>
      <label className="text-sm flex items-center gap-2">
        <input type="radio" checked={!f.use_specific_office_hours} onChange={() => set('use_specific_office_hours', false)} />
        This extension uses default office hours
      </label>
      <label className="text-sm flex items-center gap-2">
        <input type="radio" checked={!!f.use_specific_office_hours} onChange={() => set('use_specific_office_hours', true)} />
        This extension uses specific office hours
      </label>
      <label className={`text-sm flex items-center gap-2 ${!f.use_specific_office_hours ? 'opacity-60' : ''}`}>
        <input type="checkbox" checked={!!f.auto_switch_status_in_hours} onChange={e => set('auto_switch_status_in_hours', e.target.checked)} disabled={!f.use_specific_office_hours} />
        Automatically switch status to Available during office hours
      </label>
      <label className={`text-sm flex items-center gap-2 ${!f.use_specific_office_hours ? 'opacity-60' : ''}`}>
        <input type="checkbox" checked={!!f.disable_outbound_after_hours} onChange={e => set('disable_outbound_after_hours', e.target.checked)} disabled={!f.use_specific_office_hours} />
        Disable Outbound calls outside office hours
      </label>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── 5. IP Phone ─────────────────────────────────────────────────────────────
function IPPhoneTab({ extension_uuid, qc, toast, navigate }: any) {
  const phone = useQuery<any>({
    queryKey: ['user-phone', extension_uuid],
    queryFn: () => fJson(`/api/users/${extension_uuid}/phone`).catch(() => null),
  });
  const provUrl = useQuery<any>({
    queryKey: ['prov-url', phone.data?.device_uuid],
    queryFn: () => phone.data?.device_uuid ? fJson(`/api/phones/${phone.data.device_uuid}/prov-url?ttl_seconds=86400`) : null,
    enabled: !!phone.data?.device_uuid,
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showWebPwd, setShowWebPwd] = useState(false);

  const reboot = useMutation({
    mutationFn: () => fJson(`/api/phones/${phone.data.device_uuid}/reboot`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: 'admin reboot from User edit' }) }),
    onSuccess: (r: any) => toast('success', `Reboot enqueued (request ${r.request_id}, chain: ${(r.chain || []).join(' → ')})`),
    onError: (e: any) => toast('error', e.message),
  });
  const reprovision = useMutation({
    mutationFn: () => fJson(`/api/phones/${phone.data.device_uuid}/rotate-prov-secret`, { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prov-url', phone.data?.device_uuid] }); toast('success', 'Provisioning secret rotated'); },
  });
  const unassign = useMutation({
    mutationFn: () => fJson(`/api/users/${extension_uuid}/device`, { method: 'DELETE' }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['user-phone', extension_uuid] });
      qc.invalidateQueries({ queryKey: ['users-list'] });
      qc.invalidateQueries({ queryKey: ['phones-discovered'] });
      const n = (r && r.removed) || 0;
      toast('success', 'Phone removed (' + n + ' device' + (n === 1 ? '' : 's') + ' unlinked) - it will show in PnP again');
    },
    onError: (e) => toast('error', e.message),
  });
  const resetCreds = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/regenerate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sip_auth: true, deskphone_password: true }),
    }),
    onSuccess: () => toast('success', 'Credentials reset — phone will need to re-pull config'),
  });
  const copy = (s: string) => navigator.clipboard.writeText(s).then(() => toast('success', 'Copied'));

  function onDeletePhone() {
    if (window.confirm('Remove the phone from this user? It will re-appear in PnP for reassignment.')) {
      unassign.mutate();
    }
  }
  const [showAddPhone, setShowAddPhone] = useState(false);
  // No phone yet: render the empty state (and the Add-Phone modal on top). Crucially, do NOT fall
  // through to the phone-details body below — it reads phone.data.device_model etc. and blanks the
  // whole page on null. The previous `&& !showAddPhone` guard let exactly that happen once you
  // clicked Add Phone.
  if (!phone.data) return <div>
    {showAddPhone && <AddPhoneModal extension_uuid={extension_uuid} onClose={() => setShowAddPhone(false)} qc={qc} toast={toast} />}
    <div className="card p-6 text-center text-textSecondary">
      No phone assigned.
      <div className="mt-3"><button className="btn-primary inline-flex items-center gap-1" onClick={() => setShowAddPhone(true)}><Plus size={14}/> Add Phone</button></div>
    </div>
  </div>;

  return (
    <div className="space-y-4">
      {showAddPhone && <AddPhoneModal extension_uuid={extension_uuid} onClose={() => setShowAddPhone(false)} qc={qc} toast={toast} />}
      <div className="card p-4 flex items-center gap-3 flex-wrap">
        <Field label="Phone Model (Normal Phone)">
          <select className="input" value={phone.data.device_model || ''} disabled>
            <option>{phone.data.device_vendor} {phone.data.device_model}</option>
          </select>
        </Field>
        <button onClick={() => setShowAddPhone(true)} className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Add</button>
        <button onClick={() => reboot.mutate()} className="btn-primary inline-flex items-center gap-1"><RefreshCw size={14} /> Reboot</button>
        <button onClick={() => reprovision.mutate()} className="btn-primary inline-flex items-center gap-1"><RefreshCw size={14} /> Reprovision</button>
        <button onClick={onDeletePhone} disabled={unassign.isPending} className="btn-ghost text-danger inline-flex items-center gap-1 disabled:opacity-50"><Trash2 size={14} /> {unassign.isPending ? "Deleting..." : "Delete"}</button>
      </div>
      <div className="text-xs"><a href="#" className="text-primary hover:underline">See the Configuration Guide for this phone</a></div>

      <div className="card p-4 space-y-4">
        <div className="font-medium">Configuration</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Extension Number">
            <div className="flex gap-2">
              <input className="input" value={phone.data.extension || ''} disabled />
              <button onClick={() => copy(phone.data.extension || '')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="Firmware">
            <div className="text-sm">{phone.data.firmware || '—'}</div>
          </Field>
          <Field label="Auth ID *">
            <input className="input" value={phone.data.auth_id || ''} disabled />
          </Field>
          <Field label="Password">
            <div className="flex gap-2">
              <input className="input font-mono" type={showPwd ? 'text' : 'password'} value={phone.data.sip_password || '••••••••••••'} disabled />
              <button onClick={() => setShowPwd(!showPwd)} className="btn-ghost p-1">{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button onClick={() => copy(phone.data.sip_password || '')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="MAC Address">
            <div className="flex gap-2">
              <input className="input font-mono" value={phone.data.device_mac_address || ''} disabled />
              <button onClick={() => copy(phone.data.device_mac_address || '')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="Provisioning Link">
            <div className="flex gap-2">
              <input className="input font-mono text-xs" value={provUrl.data?.url || 'rotating…'} disabled />
              <button onClick={() => copy(provUrl.data?.url || '')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="Routing Device *">
            <select className="input" value={phone.data.sbc_node_id || ''} onChange={() => {}}>
              <option>{phone.data.sbc_name || 'Direct (no SBC)'}</option>
            </select>
          </Field>
          <div />
          <Field label="Web Console URL">
            {phone.data.web_console_url
              ? <a href={phone.data.web_console_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs break-all">{phone.data.web_console_url}</a>
              : <span className="text-textSecondary text-xs">—</span>}
          </Field>
          <Field label="Web Console Username">
            <div className="flex gap-2">
              <input className="input" value={phone.data.web_console_username || ''} disabled />
              <button onClick={() => copy(phone.data.web_console_username || '')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="Web Console Password">
            <div className="flex gap-2">
              <input className="input font-mono" type={showWebPwd ? 'text' : 'password'} value={phone.data.web_console_password || 'admin'} disabled />
              <button onClick={() => setShowWebPwd(!showWebPwd)} className="btn-ghost p-1">{showWebPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button onClick={() => copy(phone.data.web_console_password || 'admin')} className="btn-ghost p-1"><Copy size={14} /></button>
            </div>
          </Field>
          <div className="col-span-2">
            <button onClick={() => resetCreds.mutate()} className="text-primary hover:underline text-sm inline-flex items-center gap-1"><KeyRound size={12} /> Reset Credentials</button>
          </div>
        </div>

        <div className="font-medium pt-3 border-t border-border">Options</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Language"><select className="input"><option>English</option></select></Field>
          <Field label="Time Zone"><select className="input"><option>Use Global Time Zone</option></select></Field>
          <Field label="Date Format"><select className="input"><option>yyyy-mm-dd (2011-10-31)</option></select></Field>
          <Field label="Time Format"><select className="input"><option>24-hour clock</option></select></Field>
        </div>
      </div>
    </div>
  );
}

// ── 6. BLF ──────────────────────────────────────────────────────────────────
// Key types match the 3CX-shape Add User → BLF dropdown: Blank, Line, BLF,
// Speed dial, Custom speed dial, Shared parking, Agent login/logout,
// Change Status. The second column is a dropdown picker when the chosen type
// has a known catalog (extensions / parking lots / queues / status codes);
// otherwise it falls back to a free-text input (e.g. Custom speed dial).
const BLF_TYPES: { v: string; label: string }[] = [
  { v: 'blank',              label: 'Blank' },
  { v: 'line',               label: 'Line' },
  { v: 'blf',                label: 'BLF' },
  { v: 'speed_dial',         label: 'Speed dial' },
  { v: 'custom_speed_dial',  label: 'Custom speed dial' },
  { v: 'shared_parking',     label: 'Shared parking' },
  { v: 'agent_login',        label: 'Agent login/logout' },
  { v: 'change_status',      label: 'Change Status' },
];

const STATUS_OPTIONS = ['Available', 'Away', 'Do Not Disturb', 'Lunch', 'Business Trip'];

function BLFTab({ extension_uuid, qc, toast }: any) {
  const blf = useQuery<any[]>({
    queryKey: ['user-blf', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/blf`),
  });
  const userDetail = useQuery<any>({
    queryKey: ['user-detail-full', extension_uuid],
    queryFn: () => fJson(`/api/users/${extension_uuid}`),
  });
  // Catalogs that populate the second-column dropdown when applicable.
  const exts = useQuery<any[]>({ queryKey: ['users'], queryFn: () => fJson('/api/users') });
  const parking = useQuery<any[]>({
    queryKey: ['parking', userDetail.data?.domain_uuid],
    queryFn: () => userDetail.data?.domain_uuid
      ? fJson(`/api/system/parking?domain_uuid=${userDetail.data.domain_uuid}`).catch(() => [])
      : Promise.resolve([]),
    enabled: !!userDetail.data?.domain_uuid,
  });
  const queues = useQuery<any[]>({
    queryKey: ['ring-groups', userDetail.data?.domain_uuid],
    queryFn: () => userDetail.data?.domain_uuid
      ? fJson(`/api/tenants/${userDetail.data.domain_uuid}/ring-groups`).catch(() => [])
      : Promise.resolve([]),
    enabled: !!userDetail.data?.domain_uuid,
  });

  const [keys, setKeys] = useState<any[]>([]);
  useEffect(() => { if (blf.data) setKeys(blf.data); }, [blf.data]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/blf`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys: keys.map((k, i) => ({ ...k, key_index: i + 1 })) }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-blf', extension_uuid] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  function setKey(i: number, patch: any) {
    const next = [...keys];
    next[i] = { ...next[i], ...patch };
    setKeys(next);
  }
  function addRow() { setKeys([...keys, { target_type: 'blank', target: '', label: '' }]); }
  function delRow(i: number) { const next = [...keys]; next.splice(i, 1); setKeys(next); }
  // Ensure at least 10 visible (extensible).
  const visible = keys.length < 10 ? [...keys, ...Array(10 - keys.length).fill({ target_type: 'blank', target: '', label: '' })] : keys;

  // Resolve the catalog (and label getter) for a given target_type.
  function catalogFor(type: string): { items: any[]; getValue: (x: any) => string; getLabel: (x: any) => string } | null {
    switch (type) {
      case 'blf':
      case 'line':
      case 'speed_dial':
        return { items: exts.data || [], getValue: (e: any) => e.extension, getLabel: (e: any) => `${e.extension} — ${e.name || ''}` };
      case 'shared_parking':
        return { items: parking.data || [], getValue: (p: any) => p.code || p.lot_code || p.name, getLabel: (p: any) => `${p.code || p.lot_code || p.name} ${p.label ? '— ' + p.label : ''}` };
      case 'agent_login':
        return { items: queues.data || [], getValue: (q: any) => q.ring_group_extension || q.extension, getLabel: (q: any) => `${q.ring_group_extension || q.extension} — ${q.ring_group_name || q.name || ''}` };
      case 'change_status':
        return { items: STATUS_OPTIONS, getValue: (s: any) => s, getLabel: (s: any) => s };
      default:
        return null;
    }
  }

  return (
    <div className="card p-4 space-y-2">
      {visible.map((k, i) => {
        const cat = catalogFor(k.target_type);
        const showSecond = k.target_type && k.target_type !== 'blank';
        return (
          <div key={i} className="flex items-center gap-2">
            <GripVertical size={14} className="text-textSecondary cursor-move" />
            <span className="w-6 text-xs text-textSecondary">{i + 1}</span>
            <select className="input flex-1 max-w-xs" value={k.target_type || 'blank'} onChange={e => setKey(i, { target_type: e.target.value, target: '' })}>
              {BLF_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            {showSecond && cat && (
              <select className="input flex-1 max-w-xs" value={k.target || ''} onChange={e => setKey(i, { target: e.target.value })}>
                <option value="">— pick {BLF_TYPES.find(t => t.v === k.target_type)?.label.toLowerCase() || ''} —</option>
                {cat.items.map((item: any, idx: number) => {
                  const v = cat.getValue(item);
                  return <option key={`${v}-${idx}`} value={v}>{cat.getLabel(item)}</option>;
                })}
              </select>
            )}
            {showSecond && !cat && (
              <input className="input flex-1 max-w-xs" value={k.target || ''} onChange={e => setKey(i, { target: e.target.value })}
                     placeholder={k.target_type === 'custom_speed_dial' ? '+1...' : 'value'} />
            )}
            {showSecond && (
              <input className="input flex-1 max-w-xs" value={k.label || ''} onChange={e => setKey(i, { label: e.target.value })} placeholder="label (optional)" />
            )}
            <button onClick={() => delRow(i)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
          </div>
        );
      })}
      <div className="flex justify-between pt-2">
        <button onClick={addRow} className="btn-ghost inline-flex items-center gap-1"><Plus size={14} /> Add more BLF's</button>
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── 7. Voicemail ────────────────────────────────────────────────────────────
function VoicemailTab({ extension_uuid, qc, toast }: any) {
  const settings = useQuery<any>({
    queryKey: ['user-settings', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(settings.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings', extension_uuid] }); toast('success', 'Saved'); },
  });
  const recordOnPhone = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/record-greeting`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_name: 'Default' }),
    }),
    onSuccess: () => toast('success', 'Calling your phone — record after the beep, then hang up.'),
  });

  return (
    <div className="card p-6 space-y-4">
      <label className="text-sm flex items-center gap-2 font-medium">
        <input type="checkbox" checked={!!f.vm_enabled} onChange={e => set('vm_enabled', e.target.checked)} />
        Enable Voicemail
      </label>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field label="Voicemail Language">
            <select className="input" value={f.vm_language || 'en'} onChange={e => set('vm_language', e.target.value)}>
              <option value="en">Standard English Prompts Set</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </Field>
          <Field label="PIN Number">
            <input className="input font-mono" value={f.voicemail_pin || ''} onChange={e => set('voicemail_pin', e.target.value)} placeholder="6-digit" />
          </Field>
          <Field label="Choose Default Greeting (applies to all statuses)">
            <AudioPicker value={f.default_greeting_id || ''} onChange={v => set('default_greeting_id', v)} placeholder="— No custom greeting —" />
          </Field>
        </div>
        <div className="space-y-3">
          <Field label="Read Date/Time">
            <select className="input" value={f.vm_read_datetime || 'ampm'} onChange={e => set('vm_read_datetime', e.target.value)}>
              <option value="off">Do Not Read</option>
              <option value="long">Read in long format</option>
              <option value="ampm">Read in AM/PM Format</option>
              <option value="24h">Read in 24h Format</option>
            </select>
          </Field>
          <Field label="Email Options">
            <select className="input" value={f.vm_email_options || 'attach_keep'} onChange={e => set('vm_email_options', e.target.value)}>
              <option value="notify_only">Send email notification only</option>
              <option value="attach_keep">Send voicemail as attachment</option>
              <option value="attach_delete">Send voicemail as attachment + delete after</option>
            </select>
          </Field>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!f.vm_disable_pin_auth} onChange={e => set('vm_disable_pin_auth', e.target.checked)} />
            Disable Voicemail PIN Authentication
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!f.vm_play_caller_id} onChange={e => set('vm_play_caller_id', e.target.checked)} />
            Play Caller ID
          </label>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── 8. View — per-department visibility matrix ──────────────────────────────
function ViewTab({ extension_uuid, qc, toast }: any) {
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const grants = useQuery<any[]>({
    queryKey: ['user-visibility', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/visibility`).catch(() => []),
  });

  const [matrix, setMatrix] = useState<Record<string, any>>({});
  useEffect(() => {
    if (grants.data && tenants.data) {
      const m: Record<string, any> = {};
      for (const t of tenants.data) {
        const g = grants.data.find((x: any) => x.target_domain_uuid === t.domain_uuid);
        m[t.domain_uuid] = g || {
          show_my_calls: false, can_barge_listen_whisper: false, see_presence: false,
          see_calls: false, call_operations: false, user_operations: false,
        };
      }
      setMatrix(m);
    }
  }, [grants.data, tenants.data]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/visibility`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grants: Object.entries(matrix).map(([k, v]) => ({ target_domain_uuid: k, ...v })) }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-visibility', extension_uuid] }); toast('success', 'Saved'); },
  });

  function toggle(dom: string, col: string) {
    setMatrix({ ...matrix, [dom]: { ...matrix[dom], [col]: !matrix[dom][col] } });
  }

  const COLS: [string, string][] = [
    ['show_my_calls',            'Show My Calls'],
    ['can_barge_listen_whisper', 'Barge-in, Listen & Whisper'],
    ['see_presence',             'See Presence'],
    ['see_calls',                'See Calls'],
    ['call_operations',          'Call Operations'],
    ['user_operations',          'User Operations'],
  ];

  return (
    <div className="card p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Department</th>
              {COLS.map(([k, l]) => <th key={k} className="text-center">{l} <span className="text-textSecondary">ⓘ</span></th>)}
            </tr>
          </thead>
          <tbody>
            {(tenants.data || []).map(t => (
              <tr key={t.domain_uuid} className="hover:bg-elevated">
                <td>{t.domain_description || t.domain_name}</td>
                {COLS.map(([k]) => (
                  <td key={k} className="text-center">
                    <input type="checkbox" checked={!!matrix[t.domain_uuid]?.[k]} onChange={() => toggle(t.domain_uuid, k)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-border flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

// ── 9. Options — kitchen sink ───────────────────────────────────────────────
function OptionsTab({ extension_uuid, qc, toast }: any) {
  const settings = useQuery<any>({
    queryKey: ['user-settings', extension_uuid],
    queryFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`),
  });
  const [form, setForm] = useState<any>({});
  const f = { ...(settings.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${extension_uuid}/settings`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings', extension_uuid] }); toast('success', 'Saved'); },
  });

  return (
    <div className="card p-6 space-y-6 max-w-4xl">
      <Section title="Call Recordings">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Toggle label="Call Recording" v={!!f.record_calls} on={v => set('record_calls', v)} />
            <label className={`text-sm flex items-center gap-2 ${!f.record_calls ? 'opacity-60' : ''}`}>
              <input type="radio" disabled={!f.record_calls} checked={!!f.record_external_only} onChange={() => set('record_external_only', true)} />
              Record external calls only
            </label>
            <label className={`text-sm flex items-center gap-2 ${!f.record_calls ? 'opacity-60' : ''}`}>
              <input type="radio" disabled={!f.record_calls} checked={!f.record_external_only && !!f.record_calls} onChange={() => set('record_external_only', false)} />
              Record all calls
            </label>
          </div>
          <div className="space-y-2">
            <Toggle label="Show Call Recording"           v={!!f.show_recordings}            on={v => set('show_recordings', v)} />
            <Toggle label="Allow deletion of recordings"  v={!!f.myphone_allow_delete_recordings} on={v => set('myphone_allow_delete_recordings', v)} />
            <Toggle label="Can start and stop recordings" v={f.allow_own_recordings !== false}    on={v => set('allow_own_recordings', v)} />
            <Toggle label="Call Recording Notification"   v={!!f.record_email_notify}        on={v => set('record_email_notify', v)} />
          </div>
        </div>
      </Section>

      <Section title="Transcription">
        <Field label="Select what to transcribe">
          <select className="input max-w-md" value={f.transcribe_mode || 'department'} onChange={e => set('transcribe_mode', e.target.value)}>
            <option value="department">Use department settings</option>
            <option value="never">Don't transcribe</option>
            <option value="voicemail_only">Voicemails only</option>
            <option value="always">Always</option>
          </select>
        </Field>
      </Section>

      <Section title="Restrictions">
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Disable Extension"                  v={!!f.disable_extension}    on={v => set('disable_extension', v)} />
          <Toggle label="Disable External Calls"             v={!!f.disable_external_calls} on={v => set('disable_external_calls', v)} />
          <Toggle label="Hide call forwarding rules"         v={!!f.myphone_hide_forwardings} on={v => set('myphone_hide_forwardings', v)} />
          <Toggle label="Hide User"                          v={!!f.hide_in_phonebook}    on={v => set('hide_in_phonebook', v)} />
          <div className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!f.pin_protected} onChange={e => set('pin_protected', e.target.checked)} />
            Enable PIN Protect for
            <input type="number" className="input max-w-[80px]" min={0} max={86400}
              value={f.pin_protect_timeout_seconds || 0} onChange={e => set('pin_protect_timeout_seconds', parseInt(e.target.value) || 0)} disabled={!f.pin_protected} />
            seconds
          </div>
          <Toggle label="Screen calls by requesting caller name using an IVR" v={!!f.call_screening} on={v => set('call_screening', v)} />
        </div>
      </Section>

      <Section title="Options">
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Block remote non-tunnel connections (Insecure!)" v={!!f.block_tunnel} on={v => set('block_tunnel', v)} />
          <Toggle label="Send email notification on missed call"          v={!!f.send_email_missed_calls} on={v => set('send_email_missed_calls', v)} />
          <Toggle label="PBX delivers audio"                              v={!!f.pbx_delivers_audio}     on={v => set('pbx_delivers_audio', v)} />
          <Toggle label="Enable Hotdesking"                               v={!!f.hotdesking_enabled}     on={v => set('hotdesking_enabled', v)} />
          <Field label="SRTP Mode">
            <select className="input" value={f.srtp_mode || 'disabled'} onChange={e => set('srtp_mode', e.target.value)}>
              <option value="disabled">Disabled</option>
              <option value="preferred">Preferred</option>
              <option value="required">Required</option>
            </select>
          </Field>
          <Field label="SIP ID: Must be unique per extension">
            <input className="input" value={f.sip_id || ''} onChange={e => set('sip_id', e.target.value)} />
          </Field>
          <Toggle label="Enable Datev Client Side Integration" v={!!f.datev_enabled} on={v => set('datev_enabled', v)} />
        </div>
      </Section>

      <Section title="M365">
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Enable MS Teams Integration" v={!!f.m365_teams}     on={v => set('m365_teams', v)} />
          <Toggle label="Enable SSO"                  v={!!f.m365_sso}       on={v => set('m365_sso', v)} />
          <Toggle label="Sync Presence"               v={!!f.m365_presence}  on={v => set('m365_presence', v)} />
          <Toggle label="Sync Personal Contacts"      v={!!f.m365_contacts}  on={v => set('m365_contacts', v)} />
        </div>
      </Section>

      <Section title="Google">
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Use calendar events as presence" v={!!f.google_calendar_presence} on={v => set('google_calendar_presence', v)} />
          <Toggle label="Enable SSO"                       v={!!f.google_sso}              on={v => set('google_sso', v)} />
          <Toggle label="Sync Personal Contacts"           v={!!f.google_contacts}         on={v => set('google_contacts', v)} />
        </div>
      </Section>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function Section({ title, children }: any) {
  return (
    <div>
      <div className="text-sm font-medium border-b border-border pb-1 mb-3">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
function Toggle({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) {
  return <label className="text-sm flex items-center gap-2 py-1"><input type="checkbox" checked={v} onChange={e => on(e.target.checked)} /> {label}</label>;
}


// ── AddPhoneModal — 3CX-style "Add by MAC" (no SN required, LAN PnP) ───────
const PHONE_MODELS = [
  { group: 'Grandstream GRP series (modern)', models: ['GRP2601','GRP2602','GRP2603','GRP2604','GRP2612','GRP2613','GRP2614','GRP2615','GRP2616','GRP2624','GRP2634'] },
  { group: 'Grandstream GXP series', models: ['GXP1615','GXP1625','GXP1628','GXP1630','GXP1760','GXP1780','GXP2130','GXP2135','GXP2140','GXP2160','GXP2170'] },
  { group: 'ATA / Cordless / Other', models: ['HT801','HT802','HT812','HT814','WP820','WP822','WP825','DP720','DP750'] },
];
function ModelSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select className="input" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Select model...</option>
      {PHONE_MODELS.map(g => (
        <optgroup key={g.group} label={g.group}>
          {g.models.map(m => <option key={m} value={m}>{m}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function timeAgoShort(ts?: string) {
  if (!ts) return '-';
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Department-scoped phone PICKER for the User editor. Instead of typing a MAC, the admin sees the
// phones discovered for this user's department, whether each is free or already assigned to someone,
// and assigns one in a click - then the shared ProvisionProgressModal tracks it to registration.
function AddPhoneModal({ extension_uuid, onClose, qc, toast }: any) {
  const avail = useQuery<any>({
    queryKey: ['available-phones', extension_uuid],
    queryFn: () => fJson(`/api/users/${extension_uuid}/available-phones`),
    refetchInterval: 5000,
  });
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [model, setModel] = useState('');
  const [label, setLabel] = useState('');
  const [manual, setManual] = useState(false);
  const [manualMac, setManualMac] = useState('');
  const [provisioningMac, setProvisioningMac] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanNonce, setScanNonce] = useState(0);
  const [scanStartedAt, setScanStartedAt] = useState(0);
  const [, scanTick] = useState(0);

  // Auto-scan the user's department the moment the picker opens (and on "Scan again"): the on-site SBC
  // runs an immediate LAN sweep so a just-plugged-in / factory-reset phone shows up here within a
  // minute instead of the SBC's 5-minute cycle. available-phones already refetches every 5s.
  useEffect(() => {
    let alive = true;
    setScanning(true);
    setScanStartedAt(Date.now());
    fJson(`/api/users/${extension_uuid}/scan-department`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => {});
    const t = setTimeout(() => { if (alive) setScanning(false); }, 90000);
    return () => { alive = false; clearTimeout(t); };
  }, [extension_uuid, scanNonce]);

  // While scanning, re-render every second so the elapsed timer + progress bar visibly move.
  useEffect(() => {
    if (!scanning) return;
    const iv = setInterval(() => scanTick(n => n + 1), 1000);
    return () => clearInterval(iv);
  }, [scanning]);

  const domainUuid = avail.data?.domain_uuid;
  const phones: any[] = avail.data?.phones || [];
  // Scan window ~= up to 60s waiting for the SBC's next check-in + ~15s sweep. Ease a bar toward 92%
  // over ~75s so it visibly moves (the SBC scans across the tunnel, so there's no live per-host count
  // like the server-side Phones scan — elapsed + found is the honest signal).
  const scanElapsed = scanning && scanStartedAt ? Math.max(0, Math.floor((Date.now() - scanStartedAt) / 1000)) : 0;
  const scanPct = Math.min(92, Math.round((scanElapsed / 75) * 100));
  const picked = phones.find(p => p.id === pickedId) || null;
  const filtered = phones.filter(p =>
    !search || (p.mac || '').includes(search.toLowerCase().replace(/[^a-f0-9]/g, ''))
            || (p.model || '').toLowerCase().includes(search.toLowerCase())
            || (p.ip || '').includes(search));

  function pick(p: any) {
    setPickedId(p.id);
    setModel(p.model || '');
    setLabel('');
    setManual(false);
  }

  const assign = useMutation({
    mutationFn: async (opts: { discovered_phone_id: number; model: string; label?: string }) =>
      fJson('/api/phones/assign', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discovered_phone_id: opts.discovered_phone_id, extension_uuid, domain_uuid: domainUuid, label: opts.label, model: opts.model }),
      }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['user-phone', extension_uuid] });
      qc.invalidateQueries({ queryKey: ['users-list'] });
      if (r?.mac_address) setProvisioningMac(r.mac_address);
      else { toast('success', 'Phone assigned'); onClose(); }
    },
    onError: (e: any) => toast('error', e.message),
  });

  const assignManual = useMutation({
    mutationFn: async () => {
      const r = await fJson(`/api/users/${extension_uuid}/available-phones/manual`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac: manualMac, model }),
      });
      return assign.mutateAsync({ discovered_phone_id: r.discovered_phone_id, model, label });
    },
    onError: (e: any) => toast('error', e.message),
  });

  const manualClean = manualMac.replace(/[^a-f0-9]/gi, '');

  // Once an assignment fires, hand off to the shared provisioning progress modal.
  if (provisioningMac) {
    return <ProvisionProgressModal mac={provisioningMac} label={label || `Extension ${avail.data?.extension || ''}`}
      onClose={() => { setProvisioningMac(null); qc.invalidateQueries({ queryKey: ['user-phone', extension_uuid] }); onClose(); }} />;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full p-5 space-y-3 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Add a phone{avail.data?.extension ? ` for ext ${avail.data.extension}` : ''}</h2>
          <button onClick={onClose} className="btn-ghost p-1 text-textSecondary">Cancel</button>
        </div>
        <p className="text-xs text-textSecondary">
          Phones discovered for this user's department. Pick one - no MAC needed. Phones already
          assigned to someone else are shown so you don't grab the wrong one.
        </p>

        {!manual && (
          <>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2 top-2.5 text-textSecondary" />
                <input className="input pl-7 w-full" placeholder="Filter by MAC / model / IP" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button
                className="btn-ghost text-sm inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                disabled={scanning}
                onClick={() => setScanNonce(n => n + 1)}
                title="Ask the on-site SBC to scan its network again">
                {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {scanning ? 'Scanning…' : 'Scan again'}
              </button>
            </div>
            {scanning && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-textSecondary">
                  <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin text-primary" /> Scanning this department's network…</span>
                  <span className="font-mono">{scanElapsed}s · {phones.length} found</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-border/60 overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-1000 ease-linear" style={{ width: `${scanPct}%` }} />
                </div>
              </div>
            )}

            <div className="border border-border rounded-md overflow-auto flex-1 min-h-[8rem]">
              {avail.isLoading && <div className="p-4 text-sm text-textSecondary">Loading discovered phones...</div>}
              {!avail.isLoading && filtered.length === 0 && (
                <div className="p-4 text-sm text-textSecondary">
                  {scanning
                    ? 'Scanning for phones on this department’s network… they’ll appear here automatically.'
                    : 'No phones found on this department’s network. Make sure the phone is powered on and on the same LAN as the SBC, hit Scan again, or add one by MAC below.'}
                </div>
              )}
              {filtered.map(p => {
                const isPicked = p.id === pickedId;
                const takenByOther = p.assigned && !p.assigned_to_this_user;
                return (
                  <div key={p.id}
                    className={`px-3 py-2 border-b border-border/60 last:border-0 flex items-center gap-3 cursor-pointer hover:bg-elevated ${isPicked ? 'bg-primary/10' : ''}`}
                    onClick={() => pick(p)}>
                    <Phone size={16} className={takenByOther ? 'text-textSecondary/60 shrink-0' : 'text-primary shrink-0'} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm flex items-center gap-2">
                        <span className="font-medium">{p.model || p.vendor_key || 'Unknown model'}</span>
                        <span className="font-mono text-xs text-textSecondary">{p.mac}</span>
                      </div>
                      <div className="text-xs text-textSecondary flex flex-wrap gap-x-3">
                        {p.ip && <span>{p.ip}</span>}
                        {p.sbc_name && <span>{p.sbc_name}</span>}
                        <span>seen {timeAgoShort(p.last_seen)}</span>
                      </div>
                    </div>
                    {p.assigned_to_this_user
                      ? <span className="text-xs text-success flex items-center gap-1 shrink-0"><Check size={12} /> This user</span>
                      : takenByOther
                        ? <span className="text-xs text-warning shrink-0">Assigned to {p.assigned_to_name}</span>
                        : <span className="text-xs text-success shrink-0">Available</span>}
                  </div>
                );
              })}
            </div>

            {picked && (
              <div className="border border-primary/40 bg-primary/5 rounded-md p-3 space-y-2">
                <div className="text-sm">
                  {picked.assigned && !picked.assigned_to_this_user
                    ? <span className="text-warning">Reassign <span className="font-mono">{picked.mac}</span> from {picked.assigned_to_name} to this user?</span>
                    : <>Assign <span className="font-mono">{picked.mac}</span> to this user</>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-textSecondary mb-1">Model {picked.model ? '' : '(required - auto-detect was unknown)'}</label>
                    <ModelSelect value={model} onChange={setModel} />
                  </div>
                  <div>
                    <label className="block text-xs text-textSecondary mb-1">Label (optional)</label>
                    <input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Front desk" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    className="btn-primary disabled:opacity-50"
                    disabled={!model || !domainUuid || assign.isPending}
                    onClick={() => assign.mutate({ discovered_phone_id: picked.id, model, label })}>
                    {assign.isPending ? 'Assigning...' : (picked.assigned && !picked.assigned_to_this_user ? 'Reassign' : 'Assign')}
                  </button>
                </div>
              </div>
            )}

            <button className="text-xs text-primary hover:underline self-start" onClick={() => { setManual(true); setPickedId(null); }}>
              Phone not listed? Add it by MAC
            </button>
          </>
        )}

        {manual && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-textSecondary mb-1">Phone Model</label>
              <ModelSelect value={model} onChange={setModel} />
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">MAC Address</label>
              <input className="input font-mono" value={manualMac} onChange={e => setManualMac(e.target.value)} placeholder="000B82xxxxxx" autoFocus />
              <p className="text-xs text-textSecondary mt-1">{manualClean.length}/12 hex characters {manualClean.length === 12 ? 'ok' : ''}</p>
            </div>
            <div className="flex justify-between pt-1">
              <button className="btn-ghost" onClick={() => setManual(false)}>Back to list</button>
              <button className="btn-primary disabled:opacity-50"
                disabled={manualClean.length !== 12 || !model || !domainUuid || assignManual.isPending || assign.isPending}
                onClick={() => assignManual.mutate()}>
                {assignManual.isPending || assign.isPending ? 'Adding...' : 'Add & provision'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

